<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { dayjsKey } from '@/keys';
import { useBookingViewStore } from '@/stores/booking-view-store';
import { useScheduleStore } from '@/stores/schedule-store';
import { useUserStore } from '@/stores/user-store';
import EventPopup from '@/elements/EventPopup.vue';
import { initialEventPopupData, showEventPopup, darkenColor, hexToRgba, hhmmToMinutes, minutesToHhmm } from '@/utils';
import { Appointment, EventPopup as EventPopupType, RemoteEvent, Slot } from '@/models';
import { BookingStatus, ColourSchemes, DateFormatStrings } from '@/definitions';
import { Dayjs } from 'dayjs';

enum Weekday {
  DayOfTheWeek = 0,
  DayOfTheMonth = 1,
}

interface Props {
  activeDateRange: {
    start: string,
    end: string
  },
  events?: RemoteEvent[],
  pendingAppointments?: Appointment[],
  selectableSlots?: Slot[],
}

const props = withDefaults(defineProps<Props>(), {
  events: () => [] as RemoteEvent[],
  pendingAppointments: () => [] as Appointment[],
  selectableSlots: () => [] as Slot[],
});

const emit = defineEmits(['event-selected'])

const dj = inject(dayjsKey);

const userStore = useUserStore();
const scheduleStore = useScheduleStore();

const { firstSchedule } = storeToRefs(scheduleStore);
const { selectedEvent } = storeToRefs(useBookingViewStore());

const popup = ref<EventPopupType>({ ...initialEventPopupData });

const isDarkMode = computed(() => userStore.myColourScheme === ColourSchemes.Dark);

function onRemoteEventMouseEnter(event: MouseEvent, remoteEvent) {
  const popupEvent = {
    ...remoteEvent,
    time: {
      start: remoteEvent.start,
      end: remoteEvent.end
    },
    customData: {
      calendar_title: remoteEvent.calendar_title,
    }
  }

  popup.value = showEventPopup(event as any, popupEvent, 'right')
}

function onRemoteEventMouseLeave() {
  popup.value = { ...initialEventPopupData };
}

/**
 * Calculates grid positioning for calendar events
 */
function calculateEventGridPosition(eventStart: Dayjs, eventEnd: Dayjs, slots) {
  if (!slots.length) return null;

  // Create a quick lookup map for slot start times to grid rows
  const timeToRowMap = new Map(slots.map(s => [s.startTime, s.gridRowStart]));
  const lastRow = slots[slots.length - 1].gridRowEnd;

  // 1. Calculate grid column based on the event's position within the week
  // Get the weekdays array to determine the correct column position
  const { start } = props.activeDateRange;
  const startDate = dj(start);

  // Find the day of the week for this event (0-6, where 0 is the first day of the week)
  const eventDayOfWeek = eventStart.diff(startDate, 'day');

  // Safety check: ensure the event is within the displayed week range
  if (eventDayOfWeek < 0 || eventDayOfWeek > 6) {
    return null;
  }

  const gridColumn = eventDayOfWeek + 2; // +2 to account for the time column (column 1)

  // 2. Calculate the Start Row
  const startHourMinute = eventStart.format('HH:mm');
  const gridRowStart = timeToRowMap.get(startHourMinute);

  // 3. Calculate the End Row
  // Find the first slot that starts AT or AFTER the event ends
  // Attention: the end time could be lower than the start time on events going to or over midnight! So we must always
  // normalize the end of an event to 24 if it is 0.
  const endTime = eventEnd.format('HH:mm') === '00:00' ? '24:00' : eventEnd.format('HH:mm');
  const endSlot = slots.find(slot => slot.startTime >= endTime);
  
  const gridRowEnd = endSlot ? endSlot.gridRowStart : lastRow;

  // Return the positioning data, only if it's within calendar hours
  if (gridRowStart && gridRowEnd) {
    return {
      gridColumn,
      gridRowStart,
      gridRowEnd,
    };
  }

  return null;
}

/**
 * Get the earliest start time of all given calendar events
 */
const earliestTime = computed(() => {
  return Math.min(
    props.events.reduce(
      (p, c) => hhmmToMinutes(c.start) < p ? hhmmToMinutes(c.start) : p,
      24 * 60
    ),
    props.pendingAppointments.reduce(
      (p, c) => hhmmToMinutes(c.slots[0].start) < p ? hhmmToMinutes(c.slots[0].start) : p,
      24 * 60
    ),
    props.selectableSlots.reduce(
      (p, c) => hhmmToMinutes(c.start) < p ? hhmmToMinutes(c.start) : p,
      24 * 60
    ),
  );
});

/**
 * Get the latest end time of all given calendar events
 */
const latestTime = computed(() => {
  return Math.max(
    props.events.reduce(
      (p, c) => hhmmToMinutes(c.end) > p ? hhmmToMinutes(c.end) : p,
      0
    ),
    props.pendingAppointments.reduce(
      (p, c) => hhmmToMinutes(c.slots[0].start) + c.slots[0].duration > p
        ? hhmmToMinutes(c.slots[0].start) + c.slots[0].duration
        : p,
      0
    ),
    props.selectableSlots.reduce(
      (p, c) => hhmmToMinutes(c.start) + c.duration > p ? hhmmToMinutes(c.start) + c.duration : p,
      0
    ),
  );
});

/**
 * Get the shortest duration of all given calendar events for building the grid
 */
const shortestDuration = computed(() => {
  return Math.min(
    props.events.reduce((p, c) => Math.min(p, c.duration), 24 * 60),
    props.pendingAppointments.reduce((p, c) => Math.min(p, c.duration), 24 * 60),
    props.selectableSlots.reduce((p, c) => Math.min(p, c.duration), 24 * 60),
  );
});

/**
 * Weekdays is an array of arrays like [["SUN", 17], ["MON", 18], ..., ["SAT", 23]]
 */
const weekdays = computed(() => {
  const { start, end } = props.activeDateRange;

  // Access language setting to trigger recomputation when locale changes
  // The actual locale is set globally on dayjs, but we need this reactive dependency
  void userStore.data.settings.language;

  // Parse the start and end dates using dayjs's automatic parsing
  const startDate = dj(start);
  const endDate = dj(end);

  const days = [];
  let currentDate = startDate;

  // Loop from the start date until it's the same as the end date, inclusive
  while (!currentDate.isAfter(endDate, 'day')) {
    days.push([
      currentDate.format('ddd').toUpperCase(), // Format to 'SUN', 'MON', etc.
      currentDate.date() // Get the day of the month (e.g., 17)
    ]);

    // Move to the next day
    currentDate = currentDate.add(1, 'day');
  }

  return days;
})

/**
 * Generates an array of time slot objects, each with grid positioning info
 */
const timeSlotsForGrid = computed(() => {
  const slots = [];

  // We initialize times using the first schedule. If we don't have one (e.g. FTUE, Booker page), we initialize the
  // grid by using the minimum and maximum times from the displayed events.
  const { start_time, end_time, slot_duration, time_updated } = firstSchedule.value || {
    start_time: minutesToHhmm(earliestTime.value),
    end_time: minutesToHhmm(latestTime.value),
    slot_duration: shortestDuration.value,
    time_updated: '1970-01-01T00:00:00',
  };

  if (!start_time || !end_time || !slot_duration) return [];

  const timezoneAwareStartTime = firstSchedule.value
    ? scheduleStore.timeToFrontendTime(start_time, time_updated)
    : start_time;
  const timezoneAwareEndTime = firstSchedule.value
    ? scheduleStore.timeToFrontendTime(end_time, time_updated)
    : end_time;

  const startTime = dj(timezoneAwareStartTime, 'H:mm');
  let endTime = dj(timezoneAwareEndTime, 'H:mm');

  // If the end time is before the start time (slot spans midnight), add a day to the end time
  if (endTime.isBefore(startTime) || endTime.isSame(startTime)) {
    endTime = endTime.add(1, 'day');
  }

  let currentTime = startTime;

  // Start grid rows at 2 to leave space for the header row
  let rowIndex = 2;

  while (currentTime.isBefore(endTime)) {
    // Create the slot for the current time
    slots.push({
      // The text to display (e.g., "9 AM" or empty for non-hour slots)
      text: currentTime.minute() === 0 ? currentTime.format('h A') : '',
      // The start time in a consistent format, useful for keys
      startTime: currentTime.format('HH:mm'),
      // Grid properties
      gridRowStart: rowIndex,
      gridRowEnd: rowIndex + 1,
    });

    currentTime = currentTime.add(slot_duration, 'minute');
    rowIndex++;
  }

  return slots;
});

/**
 * Generates an array of pending appointments that fall within the active week with grid positioning info
 */
const filteredPendingAppointmentsForGrid = computed(() => {
  const slots = timeSlotsForGrid.value;
  if (!slots.length) return [];

  const { start, end } = props.activeDateRange;
  const pendingAppointmentsWithinActiveWeek = props.pendingAppointments.filter((appointment) => {
    const appointmentStart = dj(appointment.slots[0].start);
    return appointmentStart.isBetween(start, end, 'day', '[]');
  });

  return pendingAppointmentsWithinActiveWeek.map(appointment => {
    const appointmentStart = dj(appointment.slots[0].start);
    const appointmentEnd = appointmentStart.add(appointment.duration, 'minute');

    const gridPosition = calculateEventGridPosition(appointmentStart, appointmentEnd, slots);

    if (gridPosition) {
      return {
        ...appointment,
        ...gridPosition,

        // Add properties to match the remote event structure for consistent rendering
        title: appointment.title,
        start: appointment.slots[0].start,
        end: appointmentEnd.format(),
        calendar_title: appointment.calendar_title,
        calendar_color: appointment.calendar_color,
      };
    }
  }).filter(Boolean)
});

/**
 * Generates an array of remote events that fall within the active week with grid positioning info
 * Note we are still fetching monthly events to reduce the roundtrips
 */
const filteredRemoteEventsForGrid = computed(() => {
  const slots = timeSlotsForGrid.value;
  if (!slots.length) return [];

  const { start, end } = props.activeDateRange;
  const remoteEventsWithinActiveWeek = props.events.filter((remoteEvent) => dj(remoteEvent.start).isBetween(start, end))

  return remoteEventsWithinActiveWeek.map(remoteEvent => {
    const eventStart = dj(remoteEvent.start);
    const eventEnd = dj(remoteEvent.end);

    const gridPosition = calculateEventGridPosition(eventStart, eventEnd, slots);
    const hasPendingPlaceholder = filteredPendingAppointmentsForGrid.value.find(
      (a) => a.title === remoteEvent.title && eventStart.isSame(a.start)
    );

    // Only show remote event, if we have a valid grid position and if we don't already have calculated
    // a placeholder for a pending (HOLD) event
    if (gridPosition && !hasPendingPlaceholder) {
      return {
        ...remoteEvent,
        ...gridPosition,
      };
    }
  }).filter(Boolean);
});

/**
 * Generates an array of selectable time slots that fall within the active week with grid positioning info
 */
const filteredSelectableSlotsForGrid = computed(() => {
  const slots = timeSlotsForGrid.value;
  if (!slots.length) return [];

  const { start, end } = props.activeDateRange;
  const selectableSlotsWithinActiveWeek = props.selectableSlots
    .filter((slot) => {
      const slotStart = dj(slot.start);
      return slotStart.isBetween(start, end, 'day', '[]');
    })
    .filter((slot) => slot.booking_status !== BookingStatus.Booked);

  return selectableSlotsWithinActiveWeek.map(slot => {
    const slotStart = dj(slot.start);
    const slotEnd = slotStart.add(slot.duration, 'minute');

    const gridPosition = calculateEventGridPosition(slotStart, slotEnd, slots);

    if (gridPosition) {
      return {
        ...slot,
        ...gridPosition,

        // Add properties to match the remote event structure for consistent rendering
        title: slotStart.format('LT'),
        start: slot.start,
        end: slotEnd.format(),
        calendar_title: '',
        calendar_color: '58C9FF', // TODO: Use design colors
      };
    }
  }).filter(Boolean)
});
</script>

<template>
  <div class="calendar-container" :style="{ 'grid-template-rows': `auto repeat(${timeSlotsForGrid.length}, minmax(50px, min-content))` }">
    <!-- Header / First row -->
    <div class="corner-cell-block"></div>
    <div
      class="calendar-weekday-header"
      v-for="(weekday, index) in weekdays"
      :key="weekday[Weekday.DayOfTheMonth]"
      :style="{ gridColumn: index + 2 }"
    >
      <p>{{ weekday[Weekday.DayOfTheWeek] }}</p>
      <p>{{ weekday[Weekday.DayOfTheMonth] }}</p>
    </div>

    <!-- Left-side hourly blocks -->
    <div
      v-for="timeSlot in timeSlotsForGrid"
      class="time-slot-cell"
      :key="timeSlot.startTime"
      :style="{ gridRow: `${timeSlot.gridRowStart} / ${timeSlot.gridRowEnd}`, gridColumn: 1 }"
      :data-testid="`time-${timeSlot.startTime}`"
    >
      {{ timeSlot.text }}
    </div>

    <!-- Remote events -->
    <div
      v-for="remoteEvent in filteredRemoteEventsForGrid"
      :key="remoteEvent?.start"
      class="event-item"
      :style="{
        gridColumn: remoteEvent?.gridColumn,
        gridRow: `${remoteEvent?.gridRowStart} / ${remoteEvent?.gridRowEnd}`,
        backgroundColor: remoteEvent?.calendar_color,
        borderLeftColor: darkenColor(remoteEvent?.calendar_color, 30)
      }"
      @mouseenter="(event) => onRemoteEventMouseEnter(event, remoteEvent)"
      @mouseleave="onRemoteEventMouseLeave"
      :data-testid="`remote-event-${dj(remoteEvent.start).format(DateFormatStrings.Qalendar)}`"
    >
      {{ remoteEvent?.title }}
    </div>

    <!-- Pending appointments -->
    <div
      v-for="pendingAppointment in filteredPendingAppointmentsForGrid"
      :key="pendingAppointment?.id"
      class="event-item pending-appointment"
      :class="{ 'dark': isDarkMode }"
      :style="{
        gridColumn: pendingAppointment?.gridColumn,
        gridRow: `${pendingAppointment?.gridRowStart} / ${pendingAppointment?.gridRowEnd}`,
        backgroundColor: hexToRgba(pendingAppointment?.calendar_color, 0.4),
        borderColor: darkenColor(pendingAppointment?.calendar_color, 30),
      }"
      @mouseenter="(event) => onRemoteEventMouseEnter(event, pendingAppointment)"
      @mouseleave="onRemoteEventMouseLeave"
      :data-testid="`pending-appointment-${dj(pendingAppointment.start).format(DateFormatStrings.Qalendar)}`"
    >
      {{ pendingAppointment?.title }}
    </div>

    <!-- Selectable time slots -->
    <div
      v-for="slot in filteredSelectableSlotsForGrid"
      :key="slot?.id"
      class="event-item selectable-slot"
      :class="{ 'dark': isDarkMode, 'selected': (selectedEvent?.start as Dayjs)?.isSame(slot?.start) }"
      :style="{
        gridColumn: slot?.gridColumn,
        gridRow: `${slot?.gridRowStart} / ${slot?.gridRowEnd}`,
        backgroundColor: hexToRgba(slot?.calendar_color, 0.4),
      }"
      @click="emit('event-selected', slot.start)"
      :data-testid="`event-${dj(slot.start).format(DateFormatStrings.Qalendar)}`"
    >
      {{ slot?.title }}
    </div>

    <!-- Event popup (appears on remote event hover) -->
    <event-popup
      v-if="(popup.event)"
      :style="{
        display: popup.display,
        top: popup.top,
        left: popup.left,
        right: popup.right ?? 'initial',
        position: 'fixed',
        zIndex: 10,
      }"
      :event="popup.event"
      :position="popup.position || 'right'"
    />

    <!-- Inner grid vertical lines -->
    <!-- 8 instead of 7 here because we want to add border to the right most edge and we start at 2 -->
    <div
      v-for="n in 8"
      :key="`line-${n}`"
      class="vertical-line"
      :style="{ gridColumn: n + 1, gridRow: '2 / -1' }"
    ></div>

    <!-- Inner grid horizontal lines -->
    <div
      v-for="timeSlot in timeSlotsForGrid"
      :key="`h-line-${timeSlot.startTime}`"
      class="horizontal-line"
      :style="{ gridRow: timeSlot.gridRowStart, gridColumn: '2 / -1' }"
    ></div>

    <!-- Last horizontal line, dynamically calculated based on time schedule -->
    <div
      class="horizontal-line"
      :style="{
        gridRow: timeSlotsForGrid[timeSlotsForGrid.length - 1]?.gridRowStart + 1,
        gridColumn: '2 / -1'
      }"
    ></div>
  </div>
</template>

<style scoped>
@import '@/assets/styles/custom-media.pcss';

.calendar-container {
  display: grid;
  grid-template-columns: max-content repeat(7, 200px);
  justify-items: center;
  border: 1px solid var(--colour-neutral-border);
  border-radius: 1.5rem;
  background-color: var(--colour-neutral-base);
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
}

.calendar-weekday-header {
  grid-row: 1;
  position: sticky;
  top: 0;
  z-index: 3;
  padding-block: 1rem;
  text-align: center;
  font-weight: bold;
  width: 100%;
  background-color: var(--colour-neutral-base);
  color: var(--colour-ti-secondary);
  position: sticky;
  left: 0;
  min-width: 200px;

  /* Weekday */
  & :first-child {
    font-weight: normal;
    font-size: 0.68rem;
  }
}

.corner-cell-block {
  background-color: var(--colour-neutral-base);
  grid-row: 1;
  grid-column: 1;
  position: sticky;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  position: sticky;
  left: 0;
}

.time-slot-cell {
  padding-inline: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: sticky;
  left: 0;
  background-color: var(--colour-neutral-base);
  color: var(--colour-ti-secondary);
  font-size: 0.68rem;
  z-index: 9;
}

.event-item {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0.5rem;
  color: #4C4D58; /* TODO: should be --colour-ti-secondary but we don't have dark mode defined yet */
  font-size: 0.68rem;
  border-left: solid 3px;
  border-top: 1px solid var(--colour-neutral-border);
  border-radius: 3px;
  cursor: pointer;
  z-index: 1;
  min-width: 200px;
}

.pending-appointment {
  border: 2px dashed;

  &.dark {
    color: var(--colour-ti-black);
  }
}

.selectable-slot {
  border: none;
  margin: .125rem;
  width: 90%;

  &.dark {
    color: var(--colour-ti-black);
  }

  &.selected {
    background-color: #58C9FF !important; /* TODO: Update this once design is ready */
  }
}

.vertical-line {
  justify-self: flex-start;
  border-left: 1px solid var(--colour-neutral-border);
  z-index: 1;
}

.horizontal-line {
  height: 1px;
  background-color: var(--colour-neutral-border);
  width: 100%;
}

@media (--md) {
  .calendar-container {
    grid-template-columns: max-content repeat(7, minmax(0, 1fr));
    overflow-x: visible;
    padding: 1rem 1rem 1.5rem 1rem;
  }

  .calendar-weekday-header {
    position: sticky;
    left: auto;
    min-width: auto;
  }

  .corner-cell-block {
    position: sticky;
    left: auto;
    z-index: 3;
  }

  .time-slot-cell {
    position: static;
    left: auto;
    background-color: transparent;
    z-index: auto;
  }

  .event-item {
    min-width: auto;
    z-index: 4;
  }

  .vertical-line {
    z-index: 3;
  }
}
</style>
